import { enumManagers } from '@chirp/enums';
import { Alert } from 'antd';
import React from 'react';

import { Color, EnumTag } from '../../components/EnumTag';
import {
  renderTableCellDescriptionsString,
  TableCellDescriptions,
} from '../../components/TableCellDescriptions';
import {
  EmergencyPlan,
  EmergencyScenarioLabelFragment,
  EmergencyScenarioStatus,
  EmergencyScenarioTableFragment,
} from '../../graphql/api/generated';
import { PermissionKey_enum, PermissionScope_enum } from '../../graphql/hasura/generated';
import { PropertyModel } from '../Property/model';
import { UserModel } from '../User/model';

import model from './model';

export const propertyColumn = model.createColumn<EmergencyScenarioTableFragment>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'propertyId',
    model: PropertyModel,
  },
  enabled: ({ currentPermissionScope }) => (
    currentPermissionScope === PermissionScope_enum.GLOBAL ||
    currentPermissionScope === PermissionScope_enum.ORGANIZATION
  ),
  render: ({ property }) => PropertyModel.routes.renderRowLink(property),
});

export const nameColumn = model.createColumn<EmergencyScenarioLabelFragment>({
  dataIndex: 'name',
  sorter: true,
});

export const statusColumn = model.createColumn<EmergencyScenarioLabelFragment>({
  title: 'Status',
  filterOptions: {
    type: 'ENUM',
    key: 'status',
    enumTable: 'EmergencyScenarioStatus',
  },
  render: ({ status }) => {
    return (
      <EnumTag
        enumValue={status}
        colorMap={{
          [EmergencyScenarioStatus.ACTIVE]: Color.Red,
          [EmergencyScenarioStatus.TESTING]: Color.Orange,
        }}
      />
    );
  },
  renderString: ({ status }) => (
    enumManagers.EmergencyScenarioStatus.getLabel(status) || ''
  ),
});

function getTotalAccessPointsDescriptions(emergencyScenario: EmergencyScenarioTableFragment) {
  const { emergencyScenarioAccessPoints } = emergencyScenario;

  const lockdownAps = emergencyScenarioAccessPoints.filter(e => (
    e.emergencyPlan === EmergencyPlan.LOCKDOWN
  ));

  const unlockedAps = emergencyScenarioAccessPoints.filter(e => (
    e.emergencyPlan === EmergencyPlan.UNLOCKED
  ));

  return {
    'Total Lockdown': lockdownAps.length,
    'Total Unlocked': unlockedAps.length,
  };
}

export const accessPointsColumn = model.createColumn<EmergencyScenarioTableFragment>({
  title: 'Emergency Plans',
  enabled: ({ hasPermission }) => hasPermission(PermissionKey_enum.AccessPoint_Read),
  render: (emergencyScenario) => {
    if (!emergencyScenario.emergencyScenarioAccessPoints.length) {
      return 'None';
    }

    const hasConflicts = emergencyScenario.emergencyScenarioAccessPoints.some((eap) => (
      !!eap.emergencyPlanConflicts.length
    ));

    return (
      <>
        <TableCellDescriptions
          descriptions={getTotalAccessPointsDescriptions(emergencyScenario)}
        />
        {hasConflicts && (
          <Alert
            showIcon
            type='error'
            message='Conflicts detected'
            style={{ marginTop: 10 }}
          />
        )}
      </>
    );
  },
  renderString: (emergencyScenario) => {
    const descriptions = getTotalAccessPointsDescriptions(emergencyScenario);

    let descriptionsString = renderTableCellDescriptionsString(descriptions);

    const hasConflicts = emergencyScenario.emergencyScenarioAccessPoints.some((eap) => (
      !!eap.emergencyPlanConflicts.length
    ));

    if (hasConflicts) {
      descriptionsString += ` (Conflicts detected)`;
    }

    return descriptionsString;
  },
});

export const createdByColumn = model.createColumn<EmergencyScenarioTableFragment>({
  title: 'Created By',
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'createdByUserId',
    model: UserModel,
  },
  render: ({ createdByUser }) => UserModel.routes.renderRowLink(createdByUser),
});
